import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Label } from 'src/@/components/ui/label';
import { RadioGroup, RadioGroupItem } from 'src/@/components/ui/radio-group';

const LABEL_CLS = 'text-xs font-semibold uppercase tracking-wider text-gray-500';
const HINT_CLS = 'text-xs text-gray-400';

// Mantenha em sincronia com lais-ai/src/agents/realestate/identity.js → DEFAULT_AGENT_CONFIG
const DEFAULT_TOM = 'auto';
const DEFAULT_FIRST_MESSAGE_MODE = 'generic';

export default function TomConversaSection({ aiAgent, changeModuleConfig, changeTomConversa }) {
    const { t } = useTranslation('settings');
    const conversa = aiAgent?.config?.module?.realstate?.conversa || {};
    const valor = conversa.tom || DEFAULT_TOM;
    const firstMsgMode = conversa.firstMessageMode || DEFAULT_FIRST_MESSAGE_MODE;

    const OPCOES = [
        {
            value: 'auto',
            title: t('aiAgents.realstate.tomConversa.tomAutoTitle', 'Automático'),
            description: t('aiAgents.realstate.tomConversa.tomAutoDescription', 'A Laís lê o ritmo da conversa e escolhe entre direto e receptivo a cada turno.'),
            icon: 'mdi:auto-fix',
        },
        {
            value: 'direto',
            title: t('aiAgents.realstate.tomConversa.tomDiretoTitle', 'Direto'),
            description: t('aiAgents.realstate.tomConversa.tomDiretoDescription', 'Perguntas objetivas, sem rodeios. Ideal para clientes apressados que já sabem o que querem.'),
            icon: 'mdi:arrow-right-bold-outline',
        },
        {
            value: 'receptivo',
            title: t('aiAgents.realstate.tomConversa.tomReceptivoTitle', 'Receptivo'),
            description: t('aiAgents.realstate.tomConversa.tomReceptivoDescription', 'Tom acolhedor, com exemplos e mais contexto. Ideal para clientes explorando ou indecisos.'),
            icon: 'mdi:hand-heart-outline',
        },
    ];

    const FIRST_MSG_OPCOES = [
        {
            value: 'generic',
            title: t('aiAgents.realstate.tomConversa.firstMsgGenericTitle', 'Genérica'),
            description: t('aiAgents.realstate.tomConversa.firstMsgGenericDescription', '"Oi, tudo bem? Pode me contar como é o imóvel que você procura?" — sem se apresentar.'),
            icon: 'mdi:message-outline',
        },
        {
            value: 'identify_only',
            title: t('aiAgents.realstate.tomConversa.firstMsgIdentifyOnlyTitle', 'Identifica empresa'),
            description: t('aiAgents.realstate.tomConversa.firstMsgIdentifyOnlyDescription', '"Olá! Eu sou {Nome} da {Empresa}. Como posso te ajudar hoje?" — apresenta-se mas não força finalidade.'),
            icon: 'mdi:badge-account-outline',
        },
        {
            value: 'identify_and_purpose',
            title: t('aiAgents.realstate.tomConversa.firstMsgIdentifyPurposeTitle', 'Identifica + finalidade'),
            description: t('aiAgents.realstate.tomConversa.firstMsgIdentifyPurposeDescription', 'Apresenta empresa e já pergunta "quer locação, compra ou já é nosso cliente?" em mensagem fragmentada.'),
            icon: 'mdi:account-question-outline',
        },
    ];

    // Prefere o handler dedicado vindo de AgenteAiDetails.js. Cai no patch genérico
    // de módulo apenas se o pai não passou changeTomConversa (compatibilidade).
    const setTom = (v) => {
        if (typeof changeTomConversa === 'function') {
            changeTomConversa(v);
            return;
        }
        changeModuleConfig({
            conversa: {
                ...conversa,
                tom: v,
            },
        });
    };

    const setFirstMessageMode = (v) => {
        changeModuleConfig({
            conversa: {
                ...conversa,
                firstMessageMode: v,
            },
        });
    };

    return (
        <Card className="rounded-xl border border-gray-100 bg-white">
            <CardHeader>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                        <Iconify icon="mdi:chat-processing-outline" width={20} className="text-violet-600" />
                    </div>
                    <div>
                        <CardTitle>{t('aiAgents.realstate.tomConversa.cardTitle', 'Tom da Conversa')}</CardTitle>
                        <CardDescription>{t('aiAgents.realstate.tomConversa.cardDescription', 'Define como a Laís se comunica com o cliente — mais direto e objetivo, ou mais receptivo e acolhedor.')}</CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                <Label className={LABEL_CLS}>{t('aiAgents.realstate.tomConversa.tonalidadeLabel', 'Tonalidade')}</Label>
                <RadioGroup
                    value={valor}
                    onValueChange={setTom}
                    className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2"
                >
                    {OPCOES.map((opcao) => {
                        const ativo = valor === opcao.value;
                        return (
                            <div key={opcao.value}>
                                <RadioGroupItem value={opcao.value} id={`tom-${opcao.value}`} className="peer sr-only" />
                                <Label
                                    htmlFor={`tom-${opcao.value}`}
                                    className={`flex flex-col gap-2 p-4 rounded-xl border cursor-pointer transition-all ${
                                        ativo
                                            ? 'border-[#7C3AED] bg-violet-50 shadow-sm shadow-[#7C3AED]/20'
                                            : 'border-gray-200 bg-white hover:border-violet-200 hover:bg-violet-50/40'
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${ativo ? 'bg-[#7C3AED] text-white' : 'bg-gray-100 text-gray-500'}`}>
                                            <Iconify icon={opcao.icon} width={16} />
                                        </div>
                                        <span className={`text-sm font-semibold ${ativo ? 'text-[#7C3AED]' : 'text-gray-900'}`}>{opcao.title}</span>
                                    </div>
                                    <p className="text-xs text-gray-500 leading-relaxed">{opcao.description}</p>
                                </Label>
                            </div>
                        );
                    })}
                </RadioGroup>
                <p className={`${HINT_CLS} mt-3`}>
                    {t('aiAgents.realstate.tomConversa.defaultPrefix', 'Padrão: ')}<code>{DEFAULT_TOM}</code>{t('aiAgents.realstate.tomConversa.tomHintSuffix', '. Quando definido como Direto ou Receptivo, sobrescreve a decisão automática do agente em todos os turnos.')}
                </p>

                <div className="mt-6 pt-6 border-t border-gray-100">
                    <Label className={LABEL_CLS}>{t('aiAgents.realstate.tomConversa.firstMessageModeLabel', 'Comportamento da primeira mensagem')}</Label>
                    <RadioGroup
                        value={firstMsgMode}
                        onValueChange={setFirstMessageMode}
                        className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2"
                    >
                        {FIRST_MSG_OPCOES.map((opcao) => {
                            const ativo = firstMsgMode === opcao.value;
                            return (
                                <div key={opcao.value}>
                                    <RadioGroupItem value={opcao.value} id={`first-msg-${opcao.value}`} className="peer sr-only" />
                                    <Label
                                        htmlFor={`first-msg-${opcao.value}`}
                                        className={`flex flex-col gap-2 p-4 rounded-xl border cursor-pointer transition-all ${
                                            ativo
                                                ? 'border-[#7C3AED] bg-violet-50 shadow-sm shadow-[#7C3AED]/20'
                                                : 'border-gray-200 bg-white hover:border-violet-200 hover:bg-violet-50/40'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${ativo ? 'bg-[#7C3AED] text-white' : 'bg-gray-100 text-gray-500'}`}>
                                                <Iconify icon={opcao.icon} width={16} />
                                            </div>
                                            <span className={`text-sm font-semibold ${ativo ? 'text-[#7C3AED]' : 'text-gray-900'}`}>{opcao.title}</span>
                                        </div>
                                        <p className="text-xs text-gray-500 leading-relaxed">{opcao.description}</p>
                                    </Label>
                                </div>
                            );
                        })}
                    </RadioGroup>
                    <p className={`${HINT_CLS} mt-3`}>
                        {t('aiAgents.realstate.tomConversa.defaultPrefix', 'Padrão: ')}<code>{DEFAULT_FIRST_MESSAGE_MODE}</code>{t('aiAgents.realstate.tomConversa.firstMsgHintSuffix', '. Define o que a Laís diz quando o cliente abre a conversa pela primeira vez (sem histórico).')}
                    </p>
                </div>
            </CardContent>
        </Card>
    );
}
